/**
 * Warehouse Module Constants
 *
 * Status enums and badge variant mappings for consistent UI rendering.
 * Following Employee/Customer module pattern.
 */

// ===== Equipment Status =====
export const EQUIPMENT_STATUS = {
    AVAILABLE: 'available',
    IN_USE: 'in_use',
    LOANED: 'loaned',
    SOLD: 'sold',
    DAMAGED: 'damaged',
    MAINTENANCE: 'maintenance',
} as const

export const EQUIPMENT_STATUS_VARIANTS: Record<string, string> = {
    available: 'default',
    in_use: 'secondary',
    loaned: 'outline',
    sold: 'destructive',
    damaged: 'destructive',
    maintenance: 'secondary',
}

// Equipment status colors for Chip component (MUI colors)
export const EQUIPMENT_STATUS_COLORS: Record<string, string> = {
    available: 'success',
    in_use: 'info',
    loaned: 'warning',
    sold: 'error',
    damaged: 'error',
    maintenance: 'secondary',
}

// ===== Component Status =====
export const COMPONENT_STATUS = {
    AVAILABLE: 'available',
    INSTALLED: 'installed',
    SOLD: 'sold',
    DAMAGED: 'damaged',
} as const

export const COMPONENT_STATUS_VARIANTS: Record<string, string> = {
    available: 'default',
    installed: 'secondary',
    sold: 'destructive',
    damaged: 'destructive',
}

// ===== Rack Status =====
export const RACK_STATUS = {
    ACTIVE: 'active',
    PLANNED: 'planned',
    RESERVED: 'reserved',
    AVAILABLE: 'available',
    DEPRECATED: 'deprecated',
    DECOMMISSIONING: 'decommissioning',
} as const

export const RACK_STATUS_VARIANTS: Record<string, string> = {
    active: 'default',
    planned: 'secondary',
    reserved: 'outline',
    available: 'outline',
    deprecated: 'destructive',
    decommissioning: 'secondary',
}

// Rack status colors for Chip component (MUI colors)
export const RACK_STATUS_COLORS: Record<string, string> = {
    active: 'success',
    planned: 'info',
    reserved: 'warning',
    available: 'primary',
    deprecated: 'error',
    decommissioning: 'error',
}

// ===== Process Types =====
export const PROCESS_TYPE = {
    IMPORT_PURCHASE: 'import_purchase',
    IMPORT_RETURN: 'import_return',
    EXPORT_SALE: 'export_sale',
    EXPORT_LOAN: 'export_loan',
    EXPORT_DELIVERY: 'export_delivery',
    INTERNAL_TRANSFER: 'internal_transfer',
    INVENTORY_CHECK: 'inventory_check',
} as const

// ===== Process Status =====
export const PROCESS_STATUS = {
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending_approval',
    APPROVED: 'approved',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
} as const

export const PROCESS_STATUS_VARIANTS: Record<string, string> = {
    draft: 'outline',
    pending_approval: 'secondary',
    approved: 'default',
    completed: 'default',
    cancelled: 'destructive',
}

// Process status colors for Chip component (MUI colors)
export const PROCESS_STATUS_COLORS: Record<string, string> = {
    draft: 'default',
    pending_approval: 'warning',
    approved: 'info',
    completed: 'success',
    cancelled: 'error',
}

// Process type colors for Chip component (MUI colors)
export const PROCESS_TYPE_COLORS: Record<string, string> = {
    import: 'primary',
    import_purchase: 'primary',
    import_return: 'primary',
    export: 'error',
    export_sale: 'error',
    export_loan: 'warning',
    export_delivery: 'warning',
    internal_transfer: 'info',
    transfer: 'info',
    inventory_check: 'secondary',
}

// Process type labels for display
export const PROCESS_TYPE_LABELS: Record<string, string> = {
    import_purchase: 'Import (Purchase)',
    import_return: 'Import (Return)',
    export_sale: 'Export (Sale)',
    export_loan: 'Export (Loan)',
    export_delivery: 'Export (Delivery)',
    internal_transfer: 'Internal Transfer',
    inventory_check: 'Inventory Check',
    import: 'Import',
    export: 'Export',
    transfer: 'Transfer',
}

// Location type colors for Chip component (MUI colors)
export const LOCATION_TYPE_COLORS: Record<string, string> = {
    warehouse: 'primary',
    datacenter: 'info',
}

// ===== Item Condition =====
export const ITEM_CONDITION = {
    NEW: 'new',
    USED: 'used',
    REFURBISHED: 'refurbished',
    DAMAGED: 'damaged',
} as const

export const ITEM_CONDITION_VARIANTS: Record<string, string> = {
    new: 'default',
    used: 'secondary',
    refurbished: 'outline',
    damaged: 'destructive',
}

// Item condition colors for Chip component (MUI colors)
export const ITEM_CONDITION_COLORS: Record<string, string> = {
    new: 'success',
    used: 'info',
    refurbished: 'warning',
    damaged: 'error',
}

// ===== Item Type =====
export const ITEM_TYPE = {
    EQUIPMENT: 'equipment',
    COMPONENT: 'component',
} as const

export const ITEM_TYPE_COLORS: Record<string, string> = {
    equipment: 'primary',
    component: 'warning',
}

export const ITEM_TYPE_LABELS: Record<string, string> = {
    equipment: 'Equipment',
    component: 'Component',
}

// ===== Location Type =====
export const LOCATION_TYPE = {
    WAREHOUSE: 'warehouse',
    DATACENTER: 'datacenter',
} as const

// ===== Endpoint Type =====
export const ENDPOINT_TYPE = {
    LOCATION: 'Location',
    SUPPLIER: 'Supplier',
    CUSTOMER: 'Customer',
} as const

// ===== Approval Roles =====
export const APPROVAL_ROLE = {
    TECH_LEAD: 'tech_lead',
    STOREKEEPER: 'storekeeper',
} as const
